import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { UserProfile } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class ApiUsersService {
  private api = inject(ApiService);

  getCurrentUser(): Observable<UserProfile> {
    return this.api.get<UserProfile>('users/me');
  }

  getUser(id: string): Observable<UserProfile> {
    return this.api.get<UserProfile>(`users/${id}`);
  }

  updateUser(id: string, user: Partial<UserProfile>): Observable<UserProfile> {
    return this.api.patch<UserProfile>(`users/${id}`, user);
  }

  // Get current user's addresses (uses /me endpoint)
  getMyAddresses(): Observable<any[]> {
    return this.api.get<any[]>('users/me/addresses');
  }

  // Create address for current user (uses /me endpoint)
  createMyAddress(address: any): Observable<any> {
    return this.api.post<any>('users/me/addresses', address);
  }

  // Update address for current user (uses /me endpoint)
  updateMyAddress(addressId: string, address: any): Observable<any> {
    return this.api.patch<any>(`users/me/addresses/${addressId}`, address);
  }

  // Delete address for current user (uses /me endpoint)
  deleteMyAddress(addressId: string): Observable<void> {
    return this.api.delete<void>(`users/me/addresses/${addressId}`);
  }

  getUserAddresses(userId: string): Observable<any[]> {
    return this.api.get<any[]>(`users/${userId}/addresses`);
  }

  createAddress(userId: string, address: any): Observable<any> {
    return this.api.post<any>(`users/${userId}/addresses`, address);
  }

  updateAddress(userId: string, addressId: string, address: any): Observable<any> {
    return this.api.patch<any>(`users/${userId}/addresses/${addressId}`, address);
  }

  deleteAddress(userId: string, addressId: string): Observable<void> {
    return this.api.delete<void>(`users/${userId}/addresses/${addressId}`);
  }

  checkUserExistsByPhone(phone: string): Observable<{ exists: boolean; phone: string }> {
    return this.api.get<{ exists: boolean; phone: string }>(`users/phone/${phone}`);
  }

  findByPhone(phone: string): Observable<UserProfile | null> {
    return this.api.get<UserProfile | null>(`users/phone/${phone}/details`);
  }

  getAll(filters?: { limit?: number; offset?: number; role?: string }): Observable<UserProfile[]> {
    return this.api.get<UserProfile[]>('users', filters);
  }

  /** Paginated list with total count. Use for admin lazy-loading. */
  getAllPaginated(filters: {
    limit: number;
    offset: number;
    role?: string;
    serviceZoneId?: string;
  }): Observable<{
    data: UserProfile[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  }> {
    return this.api.get<{ data: UserProfile[]; total: number; limit: number; offset: number; hasMore: boolean }>(
      'users',
      filters,
      { unwrap: false }
    );
  }

  createUser(user: {
    email: string;
    name: string;
    password?: string;
    phone?: string;
    role?: string;
    posLocationId?: string;
    serviceZoneId?: string;
  }): Observable<UserProfile> {
    return this.api.post<UserProfile>('users', user);
  }

  deleteUser(id: string): Observable<void> {
    return this.api.delete<void>(`users/${id}`);
  }
}

